import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Spinner } from '../ui/spinner';
import { Alert, AlertDescription } from '../ui/alert';
import { ChevronRight, ChevronDown, Flag, Check, Locate } from 'lucide-react';
import { flaggedChatsAPI } from '../../services/api';
import { downloadFlaggedThreadJson, downloadFlaggedThreadCsv } from './flaggedChatsExport';
import { flagTypeBadgeStyle, flagTypeHex } from './flagTypeColors';
import { isBotAgent, isUnknownBotStatus, BOT_STATUS_UNKNOWN_NOTICE } from './agentLabels';
import { useBrand } from '../../context/BrandContext';

// Shared by both the Flagged Threads list and the Agent Analysis chart's per-point thread
// list — a thread should look and behave identically (expand, view/export conversation, mark
// checked) no matter which report surfaced it.

// Keyed by brand, matching PlayerProfilePage's BACKOFFICE_PLAYER_URL. The backoffice falls
// back to bitstarz for any brand without its own entry yet; the LiveChat archive deliberately
// doesn't — only brands whose chats come from LiveChat have one (Cashy's come from Pulse), and
// a link into another brand's archive is worse than no link. Use livechatArchiveThreadUrl.
export const LIVECHAT_ARCHIVE_URL = {
  bitstarz: 'https://my.livechatinc.com/archives',
};

// The thread's page in the brand's LiveChat archive, or undefined for a brand without one.
export const livechatArchiveThreadUrl = (brand, threadId) => (
  LIVECHAT_ARCHIVE_URL[brand] ? `${LIVECHAT_ARCHIVE_URL[brand]}/${encodeURIComponent(threadId)}` : undefined
);
export const BACKOFFICE_PLAYER_URL = {
  bitstarz: 'https://backoffice.bitstarz-kute.com/backend/players',
  cashy:    'https://backoffice.cashy-kaga.com/backend/players',
};

// start_at (and the date-range filter above) are anchored to UTC day boundaries — rendering
// in the browser's local timezone would shift a late-UTC thread into "the next day" for
// anyone east of UTC, making a thread the date filter correctly excluded look like it wasn't
// (see PatternTrendChart.formatBucketLabel for the same fix on the CRM report's trend chart).
export const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
  });
};

// num_messages_agent/num_messages_agent_no_bot come from livechat.f_thread and aren't
// per-agent — just overall agent-side message counts for the thread — so this is only a
// rough "how much of the agent side looked automated" signal, not an exact per-agent
// breakdown. Null when there were no agent messages to divide by, including for a brand whose
// warehouse has no message metrics at all (they come back null).
const botMessageSharePercent = (thread) => {
  if (!thread.num_messages_agent) return null;
  const botMessages = thread.num_messages_agent - thread.num_messages_agent_no_bot;
  return Math.round((botMessages / thread.num_messages_agent) * 100);
};

// -- Evidence-quote matching ---------------------------------------------------

// Loose match: lowercase, collapse whitespace, strip common quote characters — evidence_quote
// is an LLM-extracted excerpt of a message, not necessarily a byte-exact substring (it may
// trim/normalize punctuation), so an exact substring check would miss real matches too often.
const normalizeForMatch = (text) => text.toLowerCase().replace(/["']/g, '"').replace(/\s+/g, ' ').trim();

// Finds the first message containing (or contained by) the evidence quote — bidirectional
// since the quote is sometimes a trimmed fragment of the message and sometimes spans slightly
// more than one message's worth of text.
const findEvidenceMessageIndex = (messages, evidenceQuote) => {
  if (!messages || !evidenceQuote) return -1;
  const needle = normalizeForMatch(evidenceQuote);
  if (!needle) return -1;
  return messages.findIndex((m) => {
    const hay = normalizeForMatch(m.text || '');
    // The reverse check (quote spans more than this one message) is guarded by a minimum
    // length so a short message like "ok" doesn't spuriously "match" any long quote that
    // happens to contain the word "ok".
    return hay.includes(needle) || (hay.length >= 8 && needle.includes(hay));
  });
};

// -- Conversation dialog -------------------------------------------------------

// matchedFlags (when non-empty) marks this message as the evidence for one or more flags —
// a colored ring in the first match's flag color plus small badges naming every flag it
// evidences, so it's visually obvious without having to re-read the whole transcript.
const ConversationBubble = ({ message, matchedFlags }) => {
  const isCustomer = message.sent_by === 'customer';
  const isEvidence = matchedFlags && matchedFlags.length > 0;
  return (
    <div className={`flex ${isCustomer ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-lg border px-3 py-2 ${
          isCustomer
            ? 'rounded-tr-sm bg-brand-blue/10 border-brand-blue/20'
            : 'rounded-tl-sm bg-brand-green/10 border-brand-green/20'
        }`}
        style={isEvidence ? { boxShadow: `0 0 0 2px ${flagTypeHex(matchedFlags[0].flag_type)}` } : undefined}
      >
        {isEvidence && (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {matchedFlags.map((f) => (
              <span
                key={f.flag_type}
                className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                style={flagTypeBadgeStyle(f.flag_type)}
              >
                <Flag className="h-2.5 w-2.5" />{f.flag_type}
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between gap-3 mb-1">
          <span className="text-xs font-bold text-ink-dim">{isCustomer ? 'Customer' : 'Agent'}</span>
          <span className="text-[10px] text-ink-mute whitespace-nowrap">{formatDate(message.sent_at)}</span>
        </div>
        <p className="text-sm whitespace-pre-wrap">{message.text}</p>
      </div>
    </div>
  );
};

// useThreadConversation lazily fetches + caches a thread's transcript — shared by the
// conversation dialog and the export button so neither has to fetch twice.
const useThreadConversation = (threadId) => {
  const [messages, setMessages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inflightRef = useRef(null);

  const load = useCallback(async () => {
    if (messages !== null) return messages;
    if (inflightRef.current) return inflightRef.current;

    setLoading(true);
    setError('');
    inflightRef.current = (async () => {
      try {
        const resp = await flaggedChatsAPI.getThreadConversation(threadId);
        const loaded = resp.data.messages || [];
        setMessages(loaded);
        return loaded;
      } catch {
        setError('Failed to load conversation.');
        return null;
      } finally {
        setLoading(false);
        inflightRef.current = null;
      }
    })();

    return inflightRef.current;
  }, [threadId, messages]);

  return { messages, loading, error, load };
};

// Controlled (open/onOpenChange come from FlaggedThreadRow) so a flag's own "Show in
// conversation" button can open this dialog pre-scrolled to its evidence, not just the
// dialog's own generic trigger. scrollToFlagType picks which flag to land on; omitted/not
// found means "the first evidence message, if any" — still saves the generic-open case from
// scrolling top-to-bottom by hand.
const ConversationDialog = ({ threadId, conversation, flags, open, onOpenChange, scrollToFlagType }) => {
  const messageRefs = useRef({});

  useEffect(() => {
    if (open) conversation.load();
    // conversation is a fresh object every render (useThreadConversation returns a new
    // literal each call) — depending on it here would refire this effect, hence retry the
    // fetch, on every render, including the ones caused by that very fetch's loading/error
    // state changing. conversation.load is the part of it that's actually stable (useCallback,
    // deps [threadId, messages]) across repeated failures, so depend on that instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, conversation.load]);

  const matchesByMessageIndex = useMemo(() => {
    const map = {};
    if (!conversation.messages) return map;
    (flags || []).forEach((f) => {
      const idx = findEvidenceMessageIndex(conversation.messages, f.evidence_quote);
      if (idx !== -1) {
        if (!map[idx]) map[idx] = [];
        map[idx].push(f);
      }
    });
    return map;
  }, [conversation.messages, flags]);

  const matchedIndexes = useMemo(
    () => Object.keys(matchesByMessageIndex).map(Number).sort((a, b) => a - b),
    [matchesByMessageIndex],
  );

  useEffect(() => {
    if (!open || matchedIndexes.length === 0) return undefined;
    const targetIdx = scrollToFlagType != null
      ? matchedIndexes.find((idx) => matchesByMessageIndex[idx].some((f) => f.flag_type === scrollToFlagType))
      : matchedIndexes[0];
    if (targetIdx == null) return undefined;
    // The dialog needs a tick to mount/measure before scrollIntoView has anything to scroll.
    const timer = setTimeout(() => {
      messageRefs.current[targetIdx]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 150);
    return () => clearTimeout(timer);
  }, [open, matchedIndexes, matchesByMessageIndex, scrollToFlagType]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">Show conversation</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-sm">
            Conversation ID: <span className="font-mono">{threadId}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2 pr-1">
          {conversation.loading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : conversation.error ? (
            <Alert variant="error"><AlertDescription>{conversation.error}</AlertDescription></Alert>
          ) : !conversation.messages || conversation.messages.length === 0 ? (
            <div className="text-sm text-ink-dim text-center py-8">No messages.</div>
          ) : conversation.messages.map((m, i) => (
            <div key={i} ref={(el) => { messageRefs.current[i] = el; }}>
              <ConversationBubble message={m} matchedFlags={matchesByMessageIndex[i]} />
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// -- Thread detail --------------------------------------------------------------

const ThreadDetailField = ({ label, value, mono, href }) => (
  <div className="min-w-0">
    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-mute mb-1">{label}</div>
    {href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`block truncate text-cta hover:underline ${mono ? 'font-mono' : ''}`}
        title={value}
      >
        {value}
      </a>
    ) : (
      <div className={`text-ink truncate ${mono ? 'font-mono' : ''}`} title={value}>{value || '—'}</div>
    )}
  </div>
);

// Wording/variant matches livechat_mining/PatternExportButtons: CSV is metadata-only (no
// conversation text, exports immediately), JSON carries the full transcript (so it waits on
// conversation.load() the same way the "Show conversation" dialog does).
const ExportButtons = ({ thread, conversation }) => {
  const [exportingJson, setExportingJson] = useState(false);
  const [exportError, setExportError] = useState(false);

  const handleExportJson = async () => {
    setExportingJson(true);
    setExportError(false);
    try {
      const messages = await conversation.load();
      // load() returns null only on a failed fetch (a genuinely empty conversation resolves
      // to []) — downloading here would silently produce a JSON file that looks complete but
      // is missing every message.
      if (messages === null) {
        setExportError(true);
        return;
      }
      downloadFlaggedThreadJson(thread, messages);
    } finally {
      setExportingJson(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" onClick={() => downloadFlaggedThreadCsv(thread)}>
        Export metadata (CSV)
      </Button>
      <Button variant="outline" size="sm" onClick={handleExportJson} disabled={exportingJson}>
        {exportingJson ? 'Exporting…' : 'Export conversations (JSON)'}
      </Button>
      {exportError && (
        <span className="text-xs text-red-400">Failed to load conversation — export cancelled.</span>
      )}
    </div>
  );
};

// Checked is shared across every reviewer, not per-user — a plain toggle, no confirmation
// needed since unchecking is always possible if someone clicks it by mistake.
const CheckToggle = ({ checked, onToggle, busy }) => (
  <button
    type="button"
    onClick={onToggle}
    disabled={busy}
    title={checked ? 'Mark as unchecked' : 'Mark as checked'}
    className={`mt-1 h-4 w-4 shrink-0 rounded border flex items-center justify-center transition-colors disabled:opacity-50 ${
      checked ? 'bg-cta border-cta text-surface' : 'border-white/25 hover:border-white/50'
    }`}
  >
    {checked && <Check className="h-3 w-3" strokeWidth={3} />}
  </button>
);

// onToggleChecked(threadId, checked, startAt) is the caller's responsibility to persist (via
// flaggedChatsAPI.setThreadChecked) and reflect back into whatever list state it owns — this
// component only reports the intent and shows a busy state while it's in flight.
const FlaggedThreadRow = ({ thread, isExpanded, onToggle, onToggleChecked }) => {
  const { activeBrand } = useBrand();
  const conversation = useThreadConversation(thread.thread_id);
  const [togglingChecked, setTogglingChecked] = useState(false);
  const [conversationOpen, setConversationOpen] = useState(false);
  const [scrollToFlagType, setScrollToFlagType] = useState(null);

  const backofficePlayerUrl = BACKOFFICE_PLAYER_URL[activeBrand] || BACKOFFICE_PLAYER_URL.bitstarz;

  const handleToggleChecked = async () => {
    setTogglingChecked(true);
    try {
      await onToggleChecked(thread.thread_id, !thread.checked, thread.start_at);
    } finally {
      setTogglingChecked(false);
    }
  };

  // Opens the conversation dialog pre-scrolled to this flag's evidence quote, instead of
  // making the reviewer hunt for it in the full transcript.
  const handleShowInConversation = (flagType) => {
    setScrollToFlagType(flagType);
    setConversationOpen(true);
  };

  return (
    <div>
      <div className="flex items-start gap-2.5 py-3 hover:bg-surface-2/50 transition-colors">
        <CheckToggle checked={thread.checked} onToggle={handleToggleChecked} busy={togglingChecked} />
        <button
          type="button"
          onClick={() => onToggle(thread.thread_id)}
          className="flex-1 min-w-0 text-left flex items-start gap-2.5"
        >
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 mt-0.5 text-cta shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 mt-0.5 text-ink-dim shrink-0" />
          )}
          <div className="flex-1 min-w-0 pr-6">
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              {thread.flags.map((f, i) => (
                <Badge key={`${f.flag_type}-${i}`} style={flagTypeBadgeStyle(f.flag_type)}>{f.flag_type}</Badge>
              ))}
            </div>
            <p className="text-sm text-ink-dim">{thread.flags[0]?.reasoning || '—'}</p>
          </div>
          <span className="text-xs text-ink-dim whitespace-nowrap shrink-0 mt-0.5">{formatDate(thread.start_at)}</span>
        </button>
      </div>
      {isExpanded && (
        <div className="pb-4 pl-6 pr-2 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-xs">
            <ThreadDetailField
              label="Conversation ID" value={thread.thread_id} mono
              href={livechatArchiveThreadUrl(activeBrand, thread.thread_id)}
            />
            <ThreadDetailField
              label="Player Email" value={thread.email}
              href={thread.user_id != null ? `${backofficePlayerUrl}/${encodeURIComponent(thread.user_id)}` : undefined}
            />
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-mute mb-1">Agent(s)</div>
              {thread.agents.length === 0 ? (
                <div className="text-ink truncate">—</div>
              ) : (
                <div className="flex flex-wrap items-center gap-1">
                  {/* Bots are grouped into one "bot" badge regardless of how many distinct
                      bot accounts touched the thread — reviewers care whether a bot was
                      involved, not which one. */}
                  {thread.agents.some(isBotAgent) && <Badge variant="info">bot</Badge>}
                  {/* Agents the brand's warehouse doesn't classify keep their ID but are
                      marked unclassified, rather than being listed among the confirmed humans
                      — a bot leg shown as a named human agent is worse than an honest
                      "unknown". See agentLabels.isUnknownBotStatus. */}
                  {thread.agents.filter((a) => !isBotAgent(a)).map((a) => (
                    <Badge
                      key={a.agent_id}
                      variant="secondary"
                      className={isUnknownBotStatus(a) ? 'opacity-70' : undefined}
                      title={isUnknownBotStatus(a) ? BOT_STATUS_UNKNOWN_NOTICE : undefined}
                    >
                      {a.agent_id}{isUnknownBotStatus(a) ? ' (bot/human unknown)' : ''}
                    </Badge>
                  ))}
                  {botMessageSharePercent(thread) != null && (
                    <span
                      className="text-[11px] text-ink-mute whitespace-nowrap"
                      title="Share of agent-side messages sent by a bot — conversation-wide, not broken down per agent"
                    >
                      ~{botMessageSharePercent(thread)}% bot messages
                    </span>
                  )}
                </div>
              )}
            </div>
            <ThreadDetailField label="Checked by" value={thread.checked ? thread.checked_by : null} />
          </div>

          {thread.rating_comment && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-mute mb-1">Customer rating comment</div>
              <div className="text-sm italic text-ink">{thread.rating_comment}</div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <ConversationDialog
              threadId={thread.thread_id} conversation={conversation} flags={thread.flags}
              open={conversationOpen} onOpenChange={setConversationOpen} scrollToFlagType={scrollToFlagType}
            />
            <div className="ml-auto">
              <ExportButtons thread={thread} conversation={conversation} />
            </div>
          </div>

          <div className="space-y-3">
            {thread.flags.map((f, i) => (
              <div key={`${f.flag_type}-${i}`} className="rounded-lg border border-white/10 p-3 space-y-2">
                <Badge style={flagTypeBadgeStyle(f.flag_type)}>{f.flag_type}</Badge>
                {f.evidence_quote && (
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-mute">Evidence quote</div>
                      <button
                        type="button"
                        onClick={() => handleShowInConversation(f.flag_type)}
                        className="flex items-center gap-1 text-[10px] text-cta hover:underline shrink-0"
                      >
                        <Locate className="h-3 w-3" />Show in conversation
                      </button>
                    </div>
                    <div className="text-sm italic text-ink">{f.evidence_quote}</div>
                  </div>
                )}
                {f.reasoning && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-mute mb-1">Reasoning</div>
                    <div className="text-xs text-ink-dim">{f.reasoning}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlaggedThreadRow;
